import {
  createSlice,
  createAsyncThunk,
  createEntityAdapter,
  EntityState,
} from "@reduxjs/toolkit";
import { getAccountsStatistic } from "api/accountsStatistic";
import type { RootState } from "reducers/types";

export const getAccountsStatisticSelector = (state: RootState) =>
  state.entities.accountsStatistics.entities as Record<
    string,
    AccountsStatisticType
  >;

export interface AccountsStatisticPostCountType {
  drafts: number;
  scheduled: number;
  queued: number;
}

export interface AccountsStatisticExternalReviewsCountType {
  pending: number;
  approved: number;
  rejected: number;
}

export interface AccountsStatisticType {
  id: string;
  groupName: string;
  postsCount: AccountsStatisticPostCountType;
  externalReviewsCount: AccountsStatisticExternalReviewsCountType;
}

const accountsStatisticsAdapter = createEntityAdapter<AccountsStatisticType>();

type AccountsStatisticsState = EntityState<AccountsStatisticType, string> & {
  isLoading: boolean;
};

const initialState: AccountsStatisticsState =
  accountsStatisticsAdapter.getInitialState({
    isLoading: false,
  });

export const fetchAccountsStatistics = createAsyncThunk(
  "accountsStatistics/fetchAccountsStatistics",
  async ({
    accountsIds,
    groupName,
  }: {
    accountsIds: string[];
    groupName: string;
  }) => {
    try {
      const accountsStatistics = await getAccountsStatistic(accountsIds);

      return {
        ...accountsStatistics,
        id: groupName,
        groupName,
        postsCount: {
          drafts: accountsStatistics.postsCount?.drafts ?? 0,
          scheduled: accountsStatistics.postsCount?.scheduled ?? 0,
          queued: accountsStatistics.postsCount?.queued ?? 0,
        },
        externalReviewsCount: {
          pending: accountsStatistics.externalReviewsCount?.pending ?? 0,
          approved: accountsStatistics.externalReviewsCount?.approved ?? 0,
          rejected: accountsStatistics.externalReviewsCount?.rejected ?? 0,
        },
      };
    } catch (error) {
      console.log("fetchAccountGroups err", error);
      throw error;
    }
  }
);

export const accountsStatistics = createSlice({
  name: "accountsStatistics",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // GET
    builder.addCase(
      fetchAccountsStatistics.pending,
      (state: AccountsStatisticsState) => {
        state.isLoading = true;
      }
    );
    builder.addCase(fetchAccountsStatistics.fulfilled, (state, action) => {
      accountsStatisticsAdapter.upsertOne(state, action.payload);
      state.isLoading = false;
    });
    builder.addCase(
      fetchAccountsStatistics.rejected,
      (state: AccountsStatisticsState) => {
        state.isLoading = false;
      }
    );
  },
});

export const accountsStatisticsReducer = accountsStatistics.reducer;
export const accountsStatisticsActions = accountsStatistics.actions;
